import { QuestionCircleOutlined, RightOutlined } from '@ant-design/icons';
import { Table, Tooltip } from 'antd';
import React, { useContext, useState } from 'react';
import { useStore } from 'zustand';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';

import { PackType, packagesStore } from '../../../PackageStore';

import type {
  PricingTotalBreakdown,
  PricingTotals,
} from './PricingTotals.types';
import styles from './PricingTotalsRow.module.scss';

type PricingBreakdownTableProps = {
  label: string;
  tooltip: string;
  data: PricingTotalBreakdown;
  isNightStayPack: boolean;
  hasAdditionalSuffix?: boolean;
  minChargedPax?: number;
  maxAllowedPax?: number;
};

const PricingBreakdownTable: React.FC<PricingBreakdownTableProps> = ({
  label,
  tooltip,
  data,
  isNightStayPack,
  hasAdditionalSuffix = false,
  minChargedPax,
  maxAllowedPax,
}) => {
  const { currencyFormatter } = useContext(CurrencyContext);

  const guestColumnValue =
    minChargedPax !== undefined && minChargedPax >= 2
      ? data.twoAdults
      : data.oneAdult;

  const canHaveExtraGuests = maxAllowedPax === undefined || maxAllowedPax > 2;

  return (
    <div className={styles.breakdownSection}>
      {isNightStayPack ? (
        <div className={styles.breakdownLabel}>
          {label}
          <Tooltip title={tooltip}>
            <QuestionCircleOutlined className={styles.tooltipIcon} />
          </Tooltip>
        </div>
      ) : null}
      <div className={styles.breakdownGrid}>
        <div className={styles.breakdownHeader}>
          <span>1 adult</span>
          <span>2 adults</span>
          <span>Extra guest adult</span>
          <span>Extra guest child</span>
        </div>
        <div className={styles.breakdownValues}>
          <span>
            {hasAdditionalSuffix && '+'}
            {currencyFormatter(guestColumnValue)}
          </span>
          <span>
            {hasAdditionalSuffix && '+'}
            {currencyFormatter(data.twoAdults)}
          </span>
          <span>
            {canHaveExtraGuests
              ? `+${currencyFormatter(data.extraAdult)}`
              : 'Unavailable'}
          </span>
          <span>
            {canHaveExtraGuests && data.extraChild !== undefined
              ? `+${currencyFormatter(data.extraChild)}`
              : 'Unavailable'}
          </span>
        </div>
      </div>
    </div>
  );
};

type PricingTotalsRowProps = {
  data: PricingTotals;
  colSpan: number;
  minChargedPax?: number;
  maxAllowedPax?: number;
};

export const PricingTotalsRow: React.FC<PricingTotalsRowProps> = ({
  data,
  colSpan,
  minChargedPax,
  maxAllowedPax,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const packType = useStore(packagesStore, (state) => state.packType);
  const isNightStayPack = packType === PackType.NIGHT_STAY;

  return (
    <Table.Summary>
      <Table.Summary.Row>
        <Table.Summary.Cell
          index={0}
          colSpan={colSpan}
          className={styles.summaryCell}
        >
          <button
            type="button"
            className={styles.trigger}
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-expanded={isExpanded}
          >
            <RightOutlined
              className={`${styles.chevron} ${
                isExpanded ? styles.chevronExpanded : ''
              }`}
            />
            <span className={styles.triggerLabel}>
              Total activity price by guests (PAX)
              {isNightStayPack && ' and nights'}
            </span>
          </button>
        </Table.Summary.Cell>
      </Table.Summary.Row>

      {isExpanded && (
        <Table.Summary.Row>
          <Table.Summary.Cell
            index={0}
            colSpan={colSpan}
            className={styles.summaryCell}
          >
            <div className={styles.content}>
              <PricingBreakdownTable
                label="ACTIVITIES FOR 1 NIGHT"
                tooltip="Includes all activities & amenities (per stay and per night)."
                data={data.firstNight}
                isNightStayPack={isNightStayPack}
                minChargedPax={minChargedPax}
                maxAllowedPax={maxAllowedPax}
              />
              {isNightStayPack && data.additionalNight && (
                <PricingBreakdownTable
                  label="ACTIVITIES PER ADDITIONAL NIGHT"
                  tooltip="Includes only activities & amenities charged per night."
                  data={data.additionalNight}
                  isNightStayPack={isNightStayPack}
                  hasAdditionalSuffix
                  minChargedPax={minChargedPax}
                  maxAllowedPax={maxAllowedPax}
                />
              )}
            </div>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    </Table.Summary>
  );
};
